// FormsEmbed — root component for the new embed/forms bundle.
//
// Schema-driven form rendering surface that uses the embed-shared
// infrastructure (api-client, tokens). Reads a form schema from the
// API based on `formSlug` in the parsed EmbedConfig; renders fields
// per the schema JSON shape returned by the forms API.
//
// reCAPTCHA v3 is lazy-injected on first submit only (project rule #20).
//
// Inline styles only — no Tailwind. NO imports from portal/.

import { useState, useCallback, useEffect, useRef } from "react";
import { embedApiFetch } from "../../embed-shared/api-client";
import { executeRecaptchaToken, resetRecaptchaForTests } from "../../embed-shared/recaptcha";
import {
  EmbedThemeProvider,
  useEmbedTokens,
  EmbedCard,
  EmbedHeading,
  EmbedText,
  EmbedButton,
  EmbedBanner,
} from "../../embed-shared/primitives";
import { FormPreview, type PreviewValue } from "./FormPreview";

// -- Types ------------------------------------------------------------------

export interface FormsEmbedConfig {
  formSlug: string;
  title?: string;
  theme?: "light" | "dark";
  apiBase: string;
  recaptchaSiteKey?: string;
}

interface FieldSchema {
  name: string;
  title: string;
  type: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  format?: string;
  pattern?: string;
  enum?: string[];
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
  minDate?: string;
  maxDate?: string;
  // Static helper text rendered under the control; empty string = absent (spec D1).
  description?: string;
}

interface FormSchema {
  // Optional display customization. The public /schema endpoint may omit it
  // entirely, so every access must fall back to a default — never deref unguarded.
  x_display?: {
    title_default?: string;
    submit_button_label?: string;
    submit_button_sending_label?: string;
    thank_you_heading?: string;
    thank_you_body?: string;
  };
  fields: FieldSchema[];
}

type SubmitState = "idle" | "submitting" | "success" | "error";

// -- Field helpers ----------------------------------------------------------

/**
 * Canonical type for a field, mapping the legacy "string" alias to "text".
 * "note" short-circuits FIRST — before the legacy bare-enum branch — so an
 * explicit note is never reinterpreted as a select by a stray `enum` array
 * (mirrors FormPreview.canonicalType's precedence, hardened in 62deb67a;
 * TD-337). Unreachable through a validated schema (metaschema rejects enum),
 * but the manual-edit Preview and hand-edited embed configs render
 * unvalidated JSON, so the precedence must hold even for invalid input.
 * Static text is handled by dedicated note branches (render/validate/payload),
 * never the control switch.
 * Exported as the single source of truth (TD-264, §45) — the portal Preview
 * tab (FormDefinitionEditor) consumes this instead of a private copy.
 */
export function fieldType(field: FieldSchema): string {
  // "note" is an explicit, authoritative type (spec D2) — never reinterpreted
  // by a stray `enum` array (TD-337, mirrors canonicalType).
  if (field.type === "note") return "note";
  if (field.type === "string") return "text";
  // A legacy bare enum on any type renders/validates as a select.
  if ((!field.options || field.options.length === 0) && field.enum && field.enum.length > 0) {
    return "select";
  }
  return field.type;
}

/**
 * Initial controlled-value for a field by type. Notes hold no value (spec D2)
 * and return undefined so they never enter the values map. Exported so the
 * note branch is unit-testable (mirrors fieldType/payloadValueFor, TD-264 §45).
 */
export function initialValueFor(field: FieldSchema): PreviewValue | undefined {
  switch (fieldType(field)) {
    case "note":
      return undefined;
    case "checkbox":
      return false;
    case "multiselect":
      return [];
    default:
      return "";
  }
}

/**
 * Build the initial controlled-values map for a schema's fields: each field
 * seeds its type-appropriate default (see initialValueFor), and any field
 * whose default is undefined (notes, spec D2/D3) is OMITTED — the key never
 * enters the map at all. This omission (not just an undefined value) is what
 * keeps payloadValueFor's widened `PreviewValue | undefined` param honest: if
 * this guard regressed to `initial[f.name] = v` unconditionally, `"name" in
 * initial` would flip true for a note even though its value is still
 * undefined — a regression payloadValueFor's own "note" case would silently
 * mask (fix round 1, Minor).
 * Exported so the guard is unit-testable directly (mirrors fieldType/
 * initialValueFor/payloadValueFor/validateFieldValues, TD-264 §45).
 */
export function buildInitialValues(fields: FieldSchema[]): Record<string, PreviewValue> {
  const initial: Record<string, PreviewValue> = {};
  for (const f of fields) {
    const v = initialValueFor(f);
    if (v !== undefined) initial[f.name] = v;
  }
  return initial;
}

/** Allowed option values for a select/multiselect (canonical or legacy enum). */
function optionValues(field: FieldSchema): string[] {
  if (field.options && field.options.length > 0) return field.options.map((o) => o.value);
  if (field.enum && field.enum.length > 0) return field.enum;
  return [];
}

// validateFieldValues — client-side validation over the expanded field-type
// ENUM. Mirrors the server-authoritative validator (internal/forms/service/
// validator.go ValidatePayload): the server is the source of truth, this is a
// fast-feedback pre-check (rule §7 — never trust the client; both sides run).
export function validateFieldValues(
  fields: FieldSchema[],
  vals: Record<string, PreviewValue>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const type = fieldType(field);
    const raw = vals[field.name];

    // Notes carry no value and no constraints — contribute nothing (spec D3).
    if (type === "note") continue;

    if (type === "checkbox") {
      if (field.required && raw !== true) {
        errors[field.name] = `${field.title} is required`;
      }
      continue;
    }

    if (type === "multiselect") {
      const arr = Array.isArray(raw) ? raw : [];
      if (field.required && arr.length === 0) {
        errors[field.name] = `${field.title} is required`;
        continue;
      }
      const allowed = optionValues(field);
      if (arr.length > 0 && allowed.length > 0 && !arr.every((v) => allowed.includes(v))) {
        errors[field.name] = `Please select valid options for ${field.title}`;
      }
      continue;
    }

    if (type === "number") {
      const empty = raw === "" || raw == null;
      if (field.required && empty) {
        errors[field.name] = `${field.title} is required`;
        continue;
      }
      if (empty) continue;
      const n = typeof raw === "number" ? raw : Number(raw);
      if (Number.isNaN(n)) {
        errors[field.name] = `${field.title} must be a number`;
        continue;
      }
      if (field.min != null && n < field.min) {
        errors[field.name] = `${field.title} must be at least ${field.min}`;
        continue;
      }
      if (field.max != null && n > field.max) {
        errors[field.name] = `${field.title} must be at most ${field.max}`;
      }
      continue;
    }

    if (type === "date") {
      const v = typeof raw === "string" ? raw : "";
      if (field.required && !v) {
        errors[field.name] = `${field.title} is required`;
        continue;
      }
      if (!v) continue;
      if (field.minDate && v < field.minDate) {
        errors[field.name] = `${field.title} must be on or after ${field.minDate}`;
        continue;
      }
      if (field.maxDate && v > field.maxDate) {
        errors[field.name] = `${field.title} must be on or before ${field.maxDate}`;
      }
      continue;
    }

    // String family: text / email / phone / textarea / select.
    const v = (typeof raw === "string" ? raw : "").trim();
    if (field.required && !v) {
      errors[field.name] = `${field.title} is required`;
      continue;
    }
    if (v && field.minLength && v.length < field.minLength) {
      errors[field.name] = `${field.title} must be at least ${field.minLength} characters`;
      continue;
    }
    if (v && field.maxLength && v.length > field.maxLength) {
      errors[field.name] = `${field.title} must be at most ${field.maxLength} characters`;
      continue;
    }
    if (v && field.format === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      errors[field.name] = "Please enter a valid email address";
      continue;
    }
    if (v && field.pattern) {
      try {
        if (!new RegExp(field.pattern).test(v)) {
          errors[field.name] = `${field.title} format is invalid`;
        }
      } catch {
        // If regex is invalid, skip pattern validation.
      }
    }
    if (v && type === "select") {
      const allowed = optionValues(field);
      if (allowed.length > 0 && !allowed.includes(v)) {
        errors[field.name] = `Please select a valid option for ${field.title}`;
      }
    }
  }
  return errors;
}

/**
 * Coerce a field value into its JSON payload form, or undefined to omit it.
 * `raw` may be undefined: note fields never enter the values map (spec D2/D3),
 * so their looked-up value is absent at the submit call site.
 * Exported as the canonical coercion (TD-264, §45) — the portal Preview tab
 * builds its dry-run payload from this instead of a duplicated copy.
 */
export function payloadValueFor(field: FieldSchema, raw: PreviewValue | undefined): unknown {
  switch (fieldType(field)) {
    case "note":
      // Notes contribute nothing to payloads (spec D3) — omit even a stray value.
      return undefined;
    case "checkbox":
      return raw === true ? true : undefined;
    case "multiselect": {
      const arr = Array.isArray(raw) ? raw : [];
      return arr.length > 0 ? arr : undefined;
    }
    case "number": {
      if (raw === "" || raw == null) return undefined;
      const n = typeof raw === "number" ? raw : Number(raw);
      return Number.isNaN(n) ? undefined : n;
    }
    default: {
      const v = (typeof raw === "string" ? raw : "").trim();
      return v ? v : undefined;
    }
  }
}

// -- Layout helpers ----------------------------------------------------------
//
// FormsEmbed self-styles via the Phase-0 primitives + useEmbedTokens(); the
// whole tree is wrapped in <EmbedThemeProvider>. Only the few wrapper/layout
// rules that aren't owned by a primitive live here, as inline styles read off
// the active TokenSet inside the rendered component.

// reCAPTCHA lazy-inject helpers now live in embed-shared/recaptcha.ts
// (extracted TD-221 Phase 4). Re-export resetRecaptchaForTests so
// existing test imports from this module keep working.
export { resetRecaptchaForTests };

// -- Component ---------------------------------------------------------------

interface FormsEmbedProps {
  config: FormsEmbedConfig;
}

// FormsEmbed wraps the whole tree in EmbedThemeProvider so every primitive +
// the shared FormPreview can read the active TokenSet via useEmbedTokens().
// config.theme may be undefined ⇒ EmbedThemeProvider auto-resolves from the
// host's prefers-color-scheme (correct default).
export function FormsEmbed({ config }: FormsEmbedProps) {
  return (
    <EmbedThemeProvider themeOverride={config.theme}>
      <FormsEmbedInner config={config} />
    </EmbedThemeProvider>
  );
}

function FormsEmbedInner({ config }: FormsEmbedProps) {
  const t = useEmbedTokens();

  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  const [values, setValues] = useState<Record<string, PreviewValue>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState("");

  const inputRefs = useRef<Record<string, HTMLElement | null>>({});

  // Fetch form schema from API on mount.
  useEffect(() => {
    let cancelled = false;
    setSchemaLoading(true);
    setSchemaError(null);

    (async () => {
      try {
        const data = await embedApiFetch<FormSchema>(
          `/api/v2/forms/${encodeURIComponent(config.formSlug)}/schema`,
        );
        if (!cancelled && data) {
          setSchema(data);
          // Initialize values per field type (checkbox->false, multiselect->[],
          // everything else->""; notes->skipped entirely) so controlled inputs
          // start with the right shape.
          setValues(buildInitialValues(data.fields));
        }
      } catch (err) {
        if (!cancelled) {
          setSchemaError(err instanceof Error ? err.message : "Failed to load form");
        }
      } finally {
        if (!cancelled) setSchemaLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [config.formSlug]);

  const handleChange = useCallback(
    (fieldName: string, value: PreviewValue) => {
      setValues((prev) => ({ ...prev, [fieldName]: value }));
      setFieldErrors((prev) => {
        if (!prev[fieldName]) return prev;
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    },
    [],
  );

  const validateFields = useCallback(
    (fields: FieldSchema[], vals: Record<string, PreviewValue>): Record<string, string> => {
      return validateFieldValues(fields, vals);
    },
    [],
  );

  const focusFirstError = useCallback((errors: Record<string, string>, fields: FieldSchema[]) => {
    for (const f of fields) {
      if (errors[f.name]) {
        const el = inputRefs.current[f.name];
        if (el && typeof el.focus === "function") el.focus();
        return;
      }
    }
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!schema || submitState === "submitting") return;

      // Client-side validation.
      const errors = validateFields(schema.fields, values);
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setSubmitError("Please correct the highlighted fields and try again.");
        setTimeout(() => focusFirstError(errors, schema.fields), 0);
        return;
      }

      setFieldErrors({});
      setSubmitError("");
      setSubmitState("submitting");

      // Lazy reCAPTCHA (project rule #20 — only on submit, never on mount).
      let recaptchaToken = "";
      if (config.recaptchaSiteKey) {
        try {
          recaptchaToken = await executeRecaptchaToken(config.recaptchaSiteKey, "form_submit");
        } catch {
          setSubmitError("Security check failed. Please refresh and try again.");
          setSubmitState("error");
          return;
        }
      }

      // Build the typed payload (string/number/boolean/string[] per field type;
      // empty values omitted so required-field enforcement stays server-side).
      const payload: Record<string, unknown> = {};
      for (const f of schema.fields) {
        const pv = payloadValueFor(f, values[f.name]);
        if (pv !== undefined) payload[f.name] = pv;
      }

      const meta: Record<string, unknown> = {};
      if (typeof window !== "undefined") meta.source_url = window.location.href;
      if (recaptchaToken) meta.recaptcha_token = recaptchaToken;

      try {
        await embedApiFetch(
          `/api/v2/forms/${encodeURIComponent(config.formSlug)}/submissions`,
          {
            method: "POST",
            body: {
              payload,
              meta: Object.keys(meta).length > 0 ? meta : undefined,
            },
          },
        );
        setSubmitState("success");
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
        setSubmitError(msg);
        setSubmitState("error");
      }
    },
    [schema, values, submitState, config.formSlug, config.recaptchaSiteKey, validateFields, focusFirstError],
  );

  const wrapperStyle: React.CSSProperties = {
    fontFamily: t.fontFamily,
    boxSizing: "border-box",
    lineHeight: t.lineHeight.normal,
    color: t.colors.text,
    fontSize: t.fontSize.md,
    WebkitFontSmoothing: "antialiased",
    maxWidth: 600,
    margin: "0 auto",
    padding: t.spacing.md,
  };

  // -- Loading state --
  if (schemaLoading) {
    return (
      <div
        style={{ ...wrapperStyle, textAlign: "center", padding: t.spacing.xl, color: t.colors.textSecondary }}
        role="status"
      >
        Loading form...
      </div>
    );
  }

  // -- Error state --
  if (schemaError || !schema) {
    return (
      <div style={wrapperStyle}>
        <EmbedBanner tone="error">
          {schemaError ?? "Form could not be loaded. Please refresh the page."}
        </EmbedBanner>
      </div>
    );
  }

  // -- Success state --
  if (submitState === "success") {
    const xd = schema.x_display ?? {};
    return (
      <div style={wrapperStyle}>
        <EmbedCard>
          <div style={{ textAlign: "center", padding: t.spacing.lg }}>
            <div
              style={{ fontSize: t.fontSize.xxl, color: t.colors.successGreen, marginBottom: t.spacing.sm }}
              aria-hidden="true"
            >
              &#10003;
            </div>
            <EmbedHeading level={2}>{xd.thank_you_heading ?? "Thank you"}</EmbedHeading>
            <div style={{ marginTop: t.spacing.sm }}>
              <EmbedText tone="secondary" size="md">
                {xd.thank_you_body ?? "Your submission has been received."}
              </EmbedText>
            </div>
          </div>
        </EmbedCard>
      </div>
    );
  }

  // -- Form state --
  const xd = schema.x_display ?? {};
  const heading = config.title?.trim() || xd.title_default || "Contact us";
  const submitLabel = xd.submit_button_label ?? "Submit";
  const submittingLabel = xd.submit_button_sending_label ?? "Submitting...";
  const busy = submitState === "submitting";

  return (
    <div style={wrapperStyle}>
      <EmbedCard>
        <div style={{ marginBottom: t.spacing.md }}>
          <EmbedHeading level={1}>{heading}</EmbedHeading>
        </div>
        <form
          onSubmit={(e) => void handleSubmit(e)}
          noValidate
          style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}
          aria-label={heading}
        >
          <FormPreview
            fields={schema.fields}
            values={values}
            onChange={handleChange}
            errors={fieldErrors}
            disabled={busy}
            idPrefix={`aldelo-sf-${config.formSlug}`}
            setRef={(name, el) => {
              inputRefs.current[name] = el;
            }}
          />

          {submitState === "error" && submitError && (
            <EmbedBanner tone="error">{submitError}</EmbedBanner>
          )}

          <EmbedButton type="submit" disabled={busy} aria-busy={busy} loading={busy} fullWidth>
            {busy ? submittingLabel : submitLabel}
          </EmbedButton>
        </form>
      </EmbedCard>
    </div>
  );
}
